/**
 * LivenessStep - Face Liveness Check for SDK
 * ==========================================
 * Uses cloud-based Face Liveness via Amplify FaceLivenessDetector.
 *
 * Flow:
 * 1. Show intro with tips
 * 2. Create liveness session via backend
 * 3. Render AWS FaceLivenessDetector (camera + challenges)
 * 4. Get results and show pass/fail
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import {
  Scan,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  Loader2,
  Camera,
  Sun,
  Glasses,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { useReportStepError } from '../embed/EmbedContext';

// Configure Amplify with Cognito Identity Pool for unauthenticated access
const identityPoolId = process.env.REACT_APP_AWS_IDENTITY_POOL_ID;
const awsRegion = identityPoolId?.split(':')[0] || process.env.REACT_APP_AWS_REGION || 'us-east-1';

if (identityPoolId) {
  Amplify.configure({
    Auth: {
      Cognito: {
        identityPoolId: identityPoolId,
        region: awsRegion,
        allowGuestAccess: true,
      },
    },
  });
}

const isAwsConfigured = Boolean(identityPoolId);

// SDK Step wrapper for liveness check
const LivenessStep = ({ onComplete, onBack, client }) => {
  const [status, setStatus] = useState('intro'); // intro, loading, detecting, success, error
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [awsSessionId, setAwsSessionId] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  // Embed mode only: tell the host the check failed (retry stays in this step).
  useReportStepError('liveness', status === 'error' ? error : null);

  // Trigger animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Mock mode simulation for when AWS isn't configured
  // Defined before handleStartLiveness so it can be used as a dependency
  const simulateMockLiveness = useCallback(async (mockSessionId) => {
    setStatus('loading');

    // Brief delay to simulate processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      const result = await client.completeLiveness(mockSessionId, 95.0);
      if (result.is_live) {
        setStatus('success');
      } else {
        setError('Liveness check failed');
        setStatus('error');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
      setStatus('error');
    }
  }, [client]);

  // Create liveness session via backend
  const handleStartLiveness = useCallback(async () => {
    setStatus('loading');
    setError(null);

    try {
      const sessionData = await client.createLivenessSession();

      // aws_session_id is the actual AWS session ID for FaceLivenessDetector
      // session_id is our internal ID
      if (sessionData.aws_session_id) {
        setAwsSessionId(sessionData.aws_session_id);
        setSessionId(sessionData.session_id);
        setStatus('detecting');
      } else if (sessionData.is_mock) {
        // Mock mode - simulate success for demo
        setSessionId(sessionData.session_id);
        await simulateMockLiveness(sessionData.session_id);
      } else {
        throw new Error('Failed to create liveness session');
      }
    } catch (err) {
      console.error('Failed to create liveness session:', err);
      setError(err.message || 'Failed to start liveness check');
      setStatus('error');
    }
  }, [client, simulateMockLiveness]);

  // Handle FaceLivenessDetector completion
  const handleAnalysisComplete = useCallback(async () => {
    setStatus('loading');

    try {
      // Small delay to ensure AWS has processed
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Get results from backend
      const result = await client.completeLiveness(sessionId, 0); // confidence comes from AWS

      if (result.is_live) {
        setStatus('success');
      } else {
        setError('Liveness verification failed. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      console.error('Failed to get liveness results:', err);
      setError(err.message || 'Failed to verify liveness');
      setStatus('error');
    }
  }, [client, sessionId]);

  // Handle FaceLivenessDetector error
  const handleDetectorError = useCallback((err) => {
    console.error('FaceLivenessDetector error:', err);
    setError(err?.message || 'Liveness detection failed. Please try again.');
    setStatus('error');
  }, []);

  // Handle completion - notify parent
  const handleComplete = useCallback(() => {
    onComplete({
      liveness_verified: true,
      session_id: sessionId,
    });
  }, [onComplete, sessionId]);

  // Retry - create new session
  const handleRetry = useCallback(() => {
    setStatus('intro');
    setError(null);
    setSessionId(null);
    setAwsSessionId(null);
  }, []);

  // Intro screen
  if (status === 'intro') {
    return (
      <div className={`sdk-step liveness-step ${animateIn ? 'animate-in' : ''}`}>
        <div className="step-header">
          <button className="back-button" onClick={onBack} type="button">
            <ChevronLeft size={20} />
          </button>
          <div className="step-icon">
            <Scan size={24} />
          </div>
        </div>

        <h2 className="step-title">Liveness Check</h2>
        <p className="step-description">
          We'll verify you're a real person with a quick face scan.
          This helps prevent identity fraud.
        </p>

        <div className="tips-grid">
          <div className="tip-item">
            <Sun size={20} />
            <span>Good lighting</span>
          </div>
          <div className="tip-item">
            <Glasses size={20} />
            <span>Remove glasses</span>
          </div>
          <div className="tip-item">
            <Camera size={20} />
            <span>Face the camera</span>
          </div>
          <div className="tip-item">
            <Eye size={20} />
            <span>Follow prompts</span>
          </div>
        </div>

        <p className="instructions-text">
          You'll be asked to position your face in an oval and follow simple instructions
          like moving your head. This takes about 10 seconds.
        </p>

        {!isAwsConfigured && (
          <div className="mock-notice">
            Demo mode - liveness will be simulated
          </div>
        )}

        <button
          className="primary-button"
          onClick={handleStartLiveness}
          type="button"
        >
          Start Liveness Check
          <Scan size={18} />
        </button>

        <style>{`
          .liveness-step {
            padding: 24px;
          }
          .step-header {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 24px;
          }
          .back-button {
            background: none;
            border: none;
            cursor: pointer;
            padding: 8px;
            color: var(--gray-500);
            border-radius: 8px;
            transition: all 0.2s;
          }
          .back-button:hover {
            background: var(--gray-100);
            color: var(--gray-700);
          }
          .step-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            background: var(--primary);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .step-title {
            font-size: 24px;
            font-weight: 600;
            margin: 0 0 8px;
            color: var(--gray-900);
          }
          .step-description {
            color: var(--gray-500);
            margin: 0 0 24px;
            line-height: 1.5;
          }
          .tips-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-bottom: 24px;
          }
          .tip-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            background: var(--gray-50);
            border-radius: 10px;
            color: var(--gray-700);
            font-size: 14px;
          }
          .tip-item svg {
            color: var(--primary);
            flex-shrink: 0;
          }
          .instructions-text {
            color: var(--gray-500);
            font-size: 14px;
            line-height: 1.6;
            margin: 0 0 24px;
          }
          .mock-notice {
            padding: 10px 14px;
            background: rgba(var(--primary-rgb, 13, 148, 136), 0.1);
            border-radius: 8px;
            font-size: 13px;
            color: var(--primary);
            margin-bottom: 16px;
            text-align: center;
          }
          .primary-button {
            width: 100%;
            padding: 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .primary-button:hover {
            background: var(--primary-dark);
            transform: translateY(-1px);
          }
        `}</style>
      </div>
    );
  }

  // Loading screen
  if (status === 'loading') {
    return (
      <div className={`sdk-step liveness-step loading ${animateIn ? 'animate-in' : ''}`}>
        <div className="loading-content">
          <Loader2 size={48} className="spinner" />
          <h2>Preparing...</h2>
          <p>Setting up your liveness check</p>
        </div>

        <style>{`
          .liveness-step.loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
            padding: 24px;
          }
          .loading-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }
          .spinner {
            color: var(--primary);
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .loading h2 {
            font-size: 20px;
            font-weight: 600;
            margin: 0;
            color: var(--gray-900);
          }
          .loading p {
            color: var(--gray-500);
            margin: 0;
          }
        `}</style>
      </div>
    );
  }

  // AWS FaceLivenessDetector
  if (status === 'detecting' && awsSessionId) {
    return (
      <div className={`sdk-step liveness-step detecting ${animateIn ? 'animate-in' : ''}`}>
        <ThemeProvider>
          <FaceLivenessDetector
            sessionId={awsSessionId}
            region={awsRegion}
            onAnalysisComplete={handleAnalysisComplete}
            onError={handleDetectorError}
            disableStartScreen={true}
          />
        </ThemeProvider>

        <style>{`
          .liveness-step.detecting {
            padding: 0;
            min-height: 500px;
          }
          /* Override Amplify styles to match our theme */
          .liveness-step.detecting [data-amplify-theme] {
            --amplify-colors-brand-primary-10: var(--primary);
            --amplify-colors-brand-primary-80: var(--primary);
            --amplify-colors-brand-primary-90: var(--primary);
            --amplify-colors-brand-primary-100: var(--primary-dark);
          }
        `}</style>
      </div>
    );
  }

  // Success screen
  if (status === 'success') {
    return (
      <div className={`sdk-step liveness-step success ${animateIn ? 'animate-in' : ''}`}>
        <div className="success-content">
          <div className="success-icon">
            <CheckCircle2 size={48} />
          </div>
          <h2>Liveness Verified</h2>
          <p>We've confirmed you're a real person.</p>
        </div>

        <button
          className="primary-button"
          onClick={handleComplete}
          type="button"
        >
          Continue
        </button>

        <style>{`
          .liveness-step.success {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
            padding: 24px;
          }
          .success-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            margin-bottom: 32px;
          }
          .success-icon {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: var(--success);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
          .success h2 {
            font-size: 20px;
            font-weight: 600;
            margin: 0;
            color: var(--gray-900);
          }
          .success p {
            color: var(--gray-500);
            margin: 0;
          }
          .primary-button {
            width: 100%;
            padding: 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }
          .primary-button:hover {
            background: var(--primary-dark);
          }
        `}</style>
      </div>
    );
  }

  // Error screen
  if (status === 'error') {
    return (
      <div className={`sdk-step liveness-step error ${animateIn ? 'animate-in' : ''}`}>
        <div className="error-content">
          <div className="error-icon">
            <AlertCircle size={48} />
          </div>
          <h2>Verification Failed</h2>
          <p>{error || 'Something went wrong. Please try again.'}</p>
        </div>

        <div className="error-actions">
          <button
            className="secondary-button"
            onClick={onBack}
            type="button"
          >
            Go Back
          </button>
          <button
            className="primary-button"
            onClick={handleRetry}
            type="button"
          >
            <RefreshCw size={18} />
            Try Again
          </button>
        </div>

        <style>{`
          .liveness-step.error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 400px;
            text-align: center;
            padding: 24px;
          }
          .error-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            margin-bottom: 32px;
          }
          .error-icon {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            background: var(--error);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
          .error h2 {
            font-size: 20px;
            font-weight: 600;
            margin: 0;
            color: var(--gray-900);
          }
          .error p {
            color: var(--gray-500);
            margin: 0;
            max-width: 280px;
          }
          .error-actions {
            display: flex;
            gap: 12px;
            width: 100%;
          }
          .secondary-button {
            flex: 1;
            padding: 16px;
            background: var(--gray-100);
            color: var(--gray-700);
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }
          .secondary-button:hover {
            background: var(--gray-200);
          }
          .primary-button {
            flex: 1;
            padding: 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 12px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .primary-button:hover {
            background: var(--primary-dark);
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default LivenessStep;
